import { Contract } from '@ethersproject/contracts'
import { BigNumber } from '@ethersproject/bignumber'
import type { Provider } from '@ethersproject/providers'
import { ROBINHOOD_V2_ROUTERS } from 'config/robinhood/v2Routers'

const ROUTER_ABI = [
  'function getAmountsOut(uint256 amountIn,address[] path) external view returns (uint256[] amounts)',
]

export interface BestRobinhoodRoute {
  router: string
  routerName: string
  path: string[]
  amountOut: BigNumber
}

const uniquePaths = (paths: string[][]): string[][] => {
  const seen = new Set<string>()

  return paths.filter((path) => {
    const key = path.map((x) => x.toLowerCase()).join('>')
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export async function findBestRobinhoodV2Route(
  provider: Provider,
  amountIn: BigNumber,
  tokenIn: string,
  tokenOut: string,
  bridgeTokens: string[] = [],
): Promise<BestRobinhoodRoute | null> {
  const paths = uniquePaths([
    [tokenIn, tokenOut],
    ...bridgeTokens
      .filter(
        (bridge) =>
          bridge.toLowerCase() !== tokenIn.toLowerCase() &&
          bridge.toLowerCase() !== tokenOut.toLowerCase(),
      )
      .map((bridge) => [tokenIn, bridge, tokenOut]),
  ])

  const quotes = await Promise.all(
    ROBINHOOD_V2_ROUTERS.flatMap((routerConfig) =>
      paths.map(async (path): Promise<BestRobinhoodRoute | null> => {
        try {
          const router = new Contract(
            routerConfig.address,
            ROUTER_ABI,
            provider,
          )

          const amounts: BigNumber[] = await router.getAmountsOut(
            amountIn,
            path,
          )

          const amountOut = amounts[amounts.length - 1]

          if (!amountOut || amountOut.isZero()) {
            return null
          }

          return {
            router: routerConfig.address,
            routerName: routerConfig.name,
            path,
            amountOut,
          }
        } catch {
          return null
        }
      }),
    ),
  )

  return quotes
    .filter((quote): quote is BestRobinhoodRoute => Boolean(quote))
    .reduce<BestRobinhoodRoute | null>(
      (best, quote) =>
        !best || quote.amountOut.gt(best.amountOut)
          ? quote
          : best,
      null,
    )
}
